/**
 * Epic I · Marketplace: public profile (I1), credentials (I5), referrals (I7).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { useAuth, type Me } from '../../lib/auth.tsx';
import {
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  Spinner,
  Tag,
  Textarea,
  fmtDate,
} from '../../lib/ui.tsx';

interface PublicCoachCard {
  id: string;
  name: string;
  credentialBadge: boolean;
  stats: {
    activeStudents: number;
    reviewsLast90d: number;
    medianReviewTurnaroundHours: number | null;
    avgRating: number | null;
    ratingCount: number;
  };
  reviews: { rating: number; text: string; verified: boolean; createdAt: string; studentName: string }[];
}

interface Credential {
  id: string;
  title: string;
  issuer: string;
  status: 'pending' | 'approved' | 'rejected' | string;
  createdAt: string;
}

interface ReferralInfo {
  code?: string | null;
  referrals: { id: string; status: string; createdAt: string; referredUserId: string | null }[];
}

export default function MarketplacePage() {
  const { me } = useAuth();
  if (!me || !me.coachProfile) return <Spinner />;
  return <MarketplaceInner me={me} profile={me.coachProfile} />;
}

function MarketplaceInner({ me, profile }: { me: Me; profile: NonNullable<Me['coachProfile']> }) {
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const flash = (msg: string) => {
    setSaved(msg);
    window.setTimeout(() => setSaved(null), 3000);
  };

  // ── I1 · Public profile form ───────────────────────────────────────────────
  const [bio, setBio] = useState(profile.bio);
  const [specialties, setSpecialties] = useState(profile.specialties.split(',').filter(Boolean).join(', '));
  const [languages, setLanguages] = useState(profile.languages.split(',').filter(Boolean).join(', '));
  const [introVideoUrl, setIntroVideoUrl] = useState(profile.introVideoUrl ?? '');
  const [isPublic, setIsPublic] = useState(profile.isPublicProfile);

  const saveProfile = useMutation({
    mutationFn: () =>
      api('/api/marketplace/profile', {
        method: 'PUT',
        body: {
          bio,
          specialties: specialties.split(',').map((s) => s.trim()).filter(Boolean),
          languages: languages.split(',').map((s) => s.trim()).filter(Boolean),
          introVideoUrl: introVideoUrl.trim() || null,
          isPublicProfile: isPublic,
        },
      }),
    onSuccess: async () => {
      setError(null);
      flash('Profile saved');
      await refresh();
      void qc.invalidateQueries({ queryKey: ['marketplace-public-card'] });
    },
    onError: setError,
  });

  // ── I1 · Verified stats preview ────────────────────────────────────────────
  const publicCard = useQuery({
    queryKey: ['marketplace-public-card', me.id],
    queryFn: () => api<PublicCoachCard>(`/api/marketplace/coaches/${me.id}`),
    retry: false,
  });

  // ── I5 · Credentials ───────────────────────────────────────────────────────
  const credentials = useQuery({
    queryKey: ['marketplace-credentials'],
    queryFn: () => api<Credential[]>('/api/marketplace/credentials'),
  });
  const [credTitle, setCredTitle] = useState('');
  const [credIssuer, setCredIssuer] = useState('');
  const addCredential = useMutation({
    mutationFn: () => api('/api/marketplace/credentials', { body: { title: credTitle.trim(), issuer: credIssuer.trim() } }),
    onSuccess: () => {
      setError(null);
      setCredTitle('');
      setCredIssuer('');
      void qc.invalidateQueries({ queryKey: ['marketplace-credentials'] });
    },
    onError: setError,
  });

  // ── I7 · Referrals ─────────────────────────────────────────────────────────
  const referrals = useQuery({
    queryKey: ['marketplace-referrals'],
    queryFn: () => api<ReferralInfo>('/api/marketplace/referrals'),
  });
  const referralLink = referrals.data?.code ? `${location.origin}/register?ref=${referrals.data.code}` : null;

  const stats = publicCard.data?.stats;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Marketplace</h1>
        {saved && <Tag tone="green">{saved}</Tag>}
      </div>

      {/* ── I1 · Public profile ───────────────────────────────────────────── */}
      <Card
        title="Public profile"
        actions={
          isPublic ? (
            <Link to={`/coaches/${me.id}`} className="text-xs font-medium text-brand-700 hover:underline">
              View public page →
            </Link>
          ) : (
            <Tag>not listed</Tag>
          )
        }
      >
        <div className="space-y-3">
          <Field label="Bio">
            <Textarea rows={3} placeholder="What do you teach, and how?" value={bio} onChange={(e) => setBio(e.target.value)} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Specialties" hint="Comma-separated, e.g. belting, musical theatre">
              <Input value={specialties} onChange={(e) => setSpecialties(e.target.value)} />
            </Field>
            <Field label="Languages" hint="Comma-separated, e.g. en, de">
              <Input value={languages} onChange={(e) => setLanguages(e.target.value)} />
            </Field>
            <Field label="Intro video URL">
              <Input placeholder="https://…" value={introVideoUrl} onChange={(e) => setIntroVideoUrl(e.target.value)} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-brand-600" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
            List me in the directory
          </label>
          <Button disabled={saveProfile.isPending} onClick={() => saveProfile.mutate()}>
            Save profile
          </Button>
        </div>
      </Card>

      {/* verified stats preview */}
      <Card title="Verified stats (what students see)">
        {publicCard.isLoading ? (
          <Spinner />
        ) : !stats ? (
          <p className="text-xs text-slate-500">
            Your public page is not live yet — enable “List me in the directory” above to see your verified stats.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatTile label="Active students" value={String(stats.activeStudents)} />
              <StatTile
                label="Median review turnaround"
                value={stats.medianReviewTurnaroundHours !== null ? `${stats.medianReviewTurnaroundHours}h` : '—'}
              />
              <StatTile label="Rating" value={stats.avgRating !== null ? `★ ${stats.avgRating}` : '—'} />
              <StatTile label="Reviews" value={String(stats.ratingCount)} />
            </div>
            {publicCard.data && publicCard.data.reviews.length > 0 && (
              <ul className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                {publicCard.data.reviews.map((r, i) => (
                  <li key={i} className="rounded-lg bg-slate-50 p-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">★ {r.rating}</span>
                      <span className="text-xs text-slate-500">{r.studentName}</span>
                      {r.verified && <Tag tone="green">verified</Tag>}
                      <span className="ml-auto text-xs text-slate-400">{fmtDate(r.createdAt)}</span>
                    </div>
                    {r.text && <p className="mt-1 text-xs text-slate-600">{r.text}</p>}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Card>

      {/* ── I5 · Credentials ──────────────────────────────────────────────── */}
      <Card title="Credentials">
        {credentials.isLoading ? (
          <Spinner />
        ) : (
          <ul className="space-y-1">
            {(credentials.data ?? []).map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm">
                <span>
                  {c.title} {c.issuer && <span className="text-xs text-slate-500">— {c.issuer}</span>}
                </span>
                <Tag tone={c.status === 'approved' ? 'green' : c.status === 'rejected' ? 'red' : 'amber'}>{c.status}</Tag>
              </li>
            ))}
            {(credentials.data ?? []).length === 0 && (
              <p className="text-xs text-slate-500">Submit diplomas or certifications — approved ones earn a badge on your public page.</p>
            )}
          </ul>
        )}
        <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <Field label="Title">
            <Input placeholder="e.g. MA Vocal Pedagogy" value={credTitle} onChange={(e) => setCredTitle(e.target.value)} />
          </Field>
          <Field label="Issuer">
            <Input placeholder="e.g. HfM Köln" value={credIssuer} onChange={(e) => setCredIssuer(e.target.value)} />
          </Field>
          <Button disabled={!credTitle.trim() || addCredential.isPending} onClick={() => addCredential.mutate()}>
            Submit
          </Button>
        </div>
      </Card>

      {/* ── I7 · Referrals ────────────────────────────────────────────────── */}
      <Card title="Refer a coach">
        {referrals.isLoading ? (
          <Spinner />
        ) : (
          <>
            {referralLink ? (
              <div className="flex items-center gap-2">
                <Input readOnly value={referralLink} onFocus={(e) => e.currentTarget.select()} />
                <Button
                  variant="secondary"
                  className="shrink-0"
                  onClick={() => {
                    void navigator.clipboard.writeText(referralLink);
                    flash('Link copied');
                  }}
                >
                  Copy
                </Button>
              </div>
            ) : (
              <p className="text-xs text-slate-500">No referral code yet.</p>
            )}
            {(referrals.data?.referrals ?? []).length > 0 && (
              <ul className="mt-3 space-y-1 border-t border-slate-100 pt-3">
                {(referrals.data?.referrals ?? []).map((r) => (
                  <li key={r.id} className="flex items-center justify-between text-sm">
                    <span className="text-xs text-slate-500">{fmtDate(r.createdAt)}</span>
                    <Tag tone={r.status === 'rewarded' ? 'green' : 'amber'}>{r.status}</Tag>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Card>

      <ErrorText error={error} />
    </div>
  );
}
function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3 text-center">
      <p className="text-lg font-bold">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
